import { useCallback, useEffect, useMemo, useState } from 'react';
import { callDeepLink, compareWithAppVersion } from '@croquiscom-pvt/web2app';
import { useRouter } from 'next/router';

import { getAdjustedBrowsingType } from '@common/app-manager';
import { isAndroid, isIOS } from '@common/device-manager';
import { useOnceReveal } from '@common/hooks';
import { gotoLogin } from '@common/utils';

import { MESSAGES } from '../../constant/messages';
import { DEFAULT_DROPDOWN_INDEX, MAIN_DROPDOWN_GROUP_INDEX } from '../../constant/product-option';
import { useProductOptionContext } from '../../context/ProductOptionProvider';
import { getCustomInputValues } from '../../core/custom-input.core';
import { checkRequiredFieldsFilled } from '../../core/custom-input.core';
import { buildItemCard, ItemCardPayload } from '../../core/item-card.core';
import {
  extractBogoItemList,
  getItemListByOptionSelection,
  getPathIndexByOptionSelection,
} from '../../core/option-map.core';
import { getCompleteSelection } from '../../core/option-selection.core';
import { getOptionShippingTypeIds } from '../../core/option-shipping-matcher.core';
import { createOptions } from '../../core/select-box-builder.core';
import { registerWmsUserNotification } from '../../graphql/select-option.2';
import {
  PdpBrowsingType,
  WmsUserNotificationInput,
  WmsUserNotificationType,
} from '../../graphql/select-option_types.2';
import { useProductOptionLog } from '../../log/useProductOptionLog';
import { CommonConfig, OptionData, ProductOptionConfig, SelectionState } from '../../type/common.type';
import { hasRequiredFieldInCustomInputOptions } from '../../util/checkers/custom-input-field-checker';
import { isLastDropdownInGroup } from '../../util/checkers/dropdown-states-checker';
import { hasSoldOutOptionsInCurrentSelection } from '../../util/checkers/option-stock-checker';
import { checkZigzinOrVendorFlexDeliveryType } from '../../util/checkers/zigzin-checker';
import { reportProductOptionError } from '../../util/executors/error-log-send-executor';
import { showZDSToastInOption } from '../../util/toast/zds-toast-in-option';
import { productMetadataBuilder } from '../../util/transformers/product-metadata-transformer';
import { useResizeListener } from '../useResizeListener';
import { useVisibilityListerner } from '../useVisibilityListerner';

interface Props {
  context: CommonConfig;
  config: ProductOptionConfig;
  hiddenRestockNotiButton: boolean;
}

export const useMainOptions = ({ context, config, hiddenRestockNotiButton }: Props) => {
  const router = useRouter();
  const { targetRef, isRevealed } = useOnceReveal();
  const isResize = useResizeListener();
  const visibility = useVisibilityListerner();
  const {
    clickGeneralProductLandingLinkUBL,
    impressGeneralProductLandingLinkUBL,
    impressOptionUBL,
    impressSoldOutUBL,
  } = useProductOptionLog();
  const [openAnimation, setOpenAnimation] = useState(false);

  const { state, actions } = useProductOptionContext();
  const product_metadata = useMemo(
    () => productMetadataBuilder(state.option.info, config),
    [state.option.info, config],
  );
  const is_use_toast = !!router.query.use_toast;

  const catalog_product_id = product_metadata.catalog_product_id;
  const meta_matching_zigzin_product_id = product_metadata.is_zigzin_matching_product
    ? product_metadata.meta_matching_product_id
    : null;
  const meta_matching_pdp_url = product_metadata.meta_matching_pdp_url;
  const meta_matching_browsing_type = product_metadata.meta_matching_browsing_type;

  const emptyMainOption = state.option.info?.catalog_product.product_option_list.length === 0;
  const shop_id = product_metadata.shop_id;

  // 드롭다운 클릭
  const handleClickSelectBoxDropdown = useCallback(
    (group_index: number, dropdown_index: number, disabled: boolean) => {
      // 필수 입력 필드가 채워지지 않은 경우
      if (!state.ui.is_item_card_mode_active && !checkRequiredFieldsFilled(state.custom_input_states)) {
        showZDSToastInOption(MESSAGES.REQUIRED_INPUT_OPTION);
        return;
      }

      // 비활성화된 드롭다운인 경우 (상위 옵션 미선택)
      if (disabled) {
        showZDSToastInOption(MESSAGES.SELECT_UPPER_OPTION);
        return;
      }

      // 기본 드롭다운을 클릭 & 커스텀 텍스트 옵션이 있는 경우 드롭다운 열림 방지
      const isAllDropdownClosed =
        state.selection.selected_dropdown_index === DEFAULT_DROPDOWN_INDEX &&
        hasRequiredFieldInCustomInputOptions(state.option.info?.catalog_product.custom_input_option_list ?? []);

      actions.handleClickDropdown({ group_index, dropdown_index, closeAll: isAllDropdownClosed });
    },
    [
      state.ui.is_item_card_mode_active,
      state.custom_input_states,
      state.selection.selected_dropdown_index,
      state.option.info?.catalog_product.custom_input_option_list,
      actions,
    ],
  );

  // 재입고 알림 API 처리
  const processNotificationResponse = useCallback(
    async (input: WmsUserNotificationInput, display_toast: boolean) => {
      await registerWmsUserNotification({ input });

      if (input.active) {
        if (display_toast) {
          showZDSToastInOption(MESSAGES.RESTOCK_NOTIFICATION_SUCCESS);
        } else {
          actions.updateRestockNotificationStatus('succeeded');
        }
      } else {
        const message = display_toast
          ? MESSAGES.RESTOCK_NOTIFICATION_CANCELLED
          : MESSAGES.RESTOCK_NOTIFICATION_CANCELLED_TOAST;
        showZDSToastInOption(message);
      }
    },
    [actions],
  );

  // 재입고 알림 선택 완료
  const completeRestockNotificationSelection = useCallback(
    async (option: OptionData, updated_dropdown_index: number) => {
      const display_toast = Boolean(context.is_zigzag_app && is_use_toast);

      try {
        if (!context.is_logged_in) {
          config?.onRestockNotificationLoginCheckFailed?.();
          gotoLogin({ redirect_url: !context.is_zigzag_app ? window.location.href : '' });
          return;
        }

        const complete_selection = getCompleteSelection(state.selection, option, updated_dropdown_index, {
          zigzin_id: option.id,
          general_id: '',
        });

        const find_option_item = getPathIndexByOptionSelection(
          state.option.map.path_index_map.zigzin,
          complete_selection.metadata.selected_option_name_list,
        );

        if (!find_option_item) {
          throw new Error(MESSAGES.PRODUCT_ITEM_ID_NOT_FOUND);
        }

        const input = {
          product_item_id: find_option_item.id,
          active: !find_option_item.wms_notification_info?.active,
          notification_type: WmsUserNotificationType.RE_WAREHOUSED_IN,
        };

        await processNotificationResponse(input, display_toast);
        actions.updateWmsNotificationInfo(input);
      } catch (error) {
        if (display_toast) {
          showZDSToastInOption(MESSAGES.RESTOCK_NOTIFICATION_FAILED);
        } else {
          actions.updateRestockNotificationStatus('failed');
        }
      }
    },
    [
      context.is_zigzag_app,
      context.is_logged_in,
      is_use_toast,
      config,
      state.selection,
      state.option.map.path_index_map.zigzin,
      processNotificationResponse,
      actions,
    ],
  );

  // 재입고 알림
  const handleRestockNotification = useCallback(
    (selected_option: OptionData, dropdown_down_index: number) => {
      const update_index = dropdown_down_index + 1;
      // BOGO인 경우 재입고 알림 미노출이므로 항상 메인 옵션 인덱스로 체크
      const is_selection_complete = isLastDropdownInGroup(state, MAIN_DROPDOWN_GROUP_INDEX, dropdown_down_index);

      if (is_selection_complete) {
        completeRestockNotificationSelection(selected_option, update_index);
      } else {
        const shipping_ids = getOptionShippingTypeIds(state.option.info, selected_option, dropdown_down_index);
        const complete_selection = getCompleteSelection(state.selection, selected_option, update_index, shipping_ids);

        actions.continueOptionSelection({
          group_index: MAIN_DROPDOWN_GROUP_INDEX,
          dropdown_index: update_index,
          selection: complete_selection,
        });

        if (!state.ui.is_restock_selection_mode) {
          // 재입고 알림 모드 시작 인덱스 업데이트
          actions.updateRestockNotificationStartIndex(dropdown_down_index);
        }
      }
    },
    [state, completeRestockNotificationSelection, actions],
  );

  const buildItemCardWithToastAndContext = useCallback(
    (payload: Omit<ItemCardPayload, 'context'>) => {
      return buildItemCard({
        ...payload,
        context: {
          bogo_info: product_metadata.bogo_info,
          maxPurchaseQuantity: config?.maxPurchaseQuantity,
          min_order_quantity: product_metadata.min_order_quantity,
          is_order_quantity_product_type: product_metadata.is_order_quantity_product_type,
          is_order_quantity_item_type: product_metadata.is_order_quantity_item_type,
          is_store_deal_banner: product_metadata.has_store_deal_banner,
          enableSingleItemSelection: config?.enableSingleItemSelection,
        },
      });
    },
    [product_metadata, config],
  );

  // 1+n 아닌 옵션 선택 완료 처리
  const completeOptionSelection = useCallback(
    (complete_selection: SelectionState) => {
      try {
        const selected_item = getItemListByOptionSelection(state.option.map.item_list_map, complete_selection.metadata);
        const custom_input_list = getCustomInputValues(state.custom_input_states);

        if (!selected_item) {
          throw new Error(MESSAGES.PRODUCT_ITEM_NOT_FOUND);
        }
        actions.addItemCard(
          buildItemCardWithToastAndContext({ item_cards: state.item_cards, selected_item, custom_input_list }),
        );
      } catch (error) {
        reportProductOptionError({ message: '옵션 선택 완료 처리 중 오류가 발생했습니다.', originalError: error });
      }
    },
    [
      state.option.map.item_list_map,
      state.custom_input_states,
      state.item_cards,
      actions,
      buildItemCardWithToastAndContext,
    ],
  );

  // 1+n 그룹 옵션 선택 완료 처리
  const completeGroupSelection = useCallback(
    (complete_selection: SelectionState) => {
      try {
        const current_selection_item = getItemListByOptionSelection(
          state.option.map.item_list_map,
          complete_selection.metadata,
        );
        const custom_input_list = getCustomInputValues(state.custom_input_states);
        const previous_selection_items = extractBogoItemList(
          state.option.map.item_list_map,
          complete_selection.metadata.previous_group_selections,
        );

        if (!current_selection_item) {
          throw new Error(MESSAGES.PRODUCT_ITEM_NOT_FOUND);
        }

        // BOGO의 경우 첫 번째 선택이 Main, 나머지가 bogo_item_list가 되도록 순서 정렬
        const all_items = [...previous_selection_items, current_selection_item];
        const selected_item = all_items[0];
        const bogo_item_list = all_items.slice(1);

        actions.addItemCard(
          buildItemCardWithToastAndContext({
            item_cards: state.item_cards,
            selected_item,
            custom_input_list,
            bogo_item_list,
          }),
        );
      } catch (error) {
        reportProductOptionError({
          message: '1+n 그룹 옵션 선택 완료 처리 중 오류가 발생했습니다.',
          originalError: error,
        });
      }
    },
    [
      state.option.map.item_list_map,
      state.custom_input_states,
      state.item_cards,
      actions,
      buildItemCardWithToastAndContext,
    ],
  );

  // 다음 그룹 옵션으로 이동
  const continueGroupSelection = useCallback(
    (group_index: number, complete_selection: SelectionState) => {
      const next_group_index = group_index + 1;
      const previous_group_selections = state.selection.metadata.previous_group_selections || [];
      const current_group_selection = {
        group_index: group_index,
        selected_option_name_list: complete_selection.metadata.selected_option_name_list,
        selected_zigzin_option_id_list: complete_selection.metadata.selected_zigzin_option_id_list,
        selected_general_option_id_list: complete_selection.metadata.selected_general_option_id_list,
        last_selected_shipping_type: complete_selection.metadata.last_selected_shipping_type,
      };

      // 1+n 대응: 이전 그룹 선택 정보를 저장 후 다음 그룹으로 이동
      actions.continueOptionSelection({
        group_index: next_group_index,
        dropdown_index: 0,
        selection: {
          selected_group_index: next_group_index,
          selected_dropdown_index: 0,
          metadata: {
            selected_option_name_list: [],
            selected_zigzin_option_id_list: [],
            selected_general_option_id_list: [],
            last_selected_shipping_type: null,
            previous_group_selections: [...previous_group_selections, current_group_selection],
          },
        },
      });
    },
    [state.selection.metadata.previous_group_selections, actions],
  );

  // 옵션 선택 분기 처리 (다음 그룹 / 다음 옵션 / 선택 완료)
  const processOptionSelection = useCallback(
    (selected_option: OptionData, group_index: number, selected_dropdown_index: number) => {
      const update_dropdown_index = selected_dropdown_index + 1;
      const shipping_ids = getOptionShippingTypeIds(state.option.info, selected_option, selected_dropdown_index);
      const complete_selection = getCompleteSelection(
        state.selection,
        selected_option,
        update_dropdown_index,
        shipping_ids,
      );

      const is_selection_complete_in_dropdown = isLastDropdownInGroup(state, group_index, selected_dropdown_index);

      if (is_selection_complete_in_dropdown && product_metadata.is_bogo) {
        const is_selection_complete_in_group = product_metadata.bogo_required_quantity === group_index;

        if (is_selection_complete_in_group) {
          completeGroupSelection(complete_selection);
        } else {
          continueGroupSelection(group_index, complete_selection);
        }
      } else if (is_selection_complete_in_dropdown) {
        completeOptionSelection(complete_selection);
      } else {
        // 다음 옵션 선택 처리
        actions.continueOptionSelection({
          group_index,
          dropdown_index: update_dropdown_index,
          selection: complete_selection,
        });
      }
    },
    [
      state,
      product_metadata.is_bogo,
      product_metadata.bogo_required_quantity,
      completeGroupSelection,
      continueGroupSelection,
      completeOptionSelection,
      actions,
    ],
  );

  // 옵션 선택
  const handleOptionSelect = useCallback(
    (selected_option: OptionData, group_index: number, dropdown_index: number) => {
      const selection_context = {
        is_last_option: isLastDropdownInGroup(state, group_index, dropdown_index),
        is_sold_out: selected_option.stock_status?.is_sold_out,
        is_restock_selection_mode: state.ui.is_restock_selection_mode,
      };

      const should_block_option_selection =
        Boolean(selection_context.is_sold_out) &&
        (selection_context.is_last_option || !selection_context.is_restock_selection_mode);

      // 옵션 선택을 차단해야 하는지 확인
      if (should_block_option_selection) {
        return;
      }

      // 재입고 알림 모드 처리
      if (state.ui.is_restock_selection_mode) {
        handleRestockNotification(selected_option, dropdown_index);
        return;
      }

      // 옵션 선택 처리
      processOptionSelection(selected_option, group_index, dropdown_index);
    },
    [state, handleRestockNotification, processOptionSelection],
  );

  // 일반 상품 랜딩 링크 클릭
  const handleGeneralProductLandingLinkClick = useCallback(() => {
    if (!meta_matching_pdp_url) {
      return;
    }

    clickGeneralProductLandingLinkUBL(catalog_product_id, meta_matching_zigzin_product_id);

    if (meta_matching_browsing_type && meta_matching_browsing_type !== PdpBrowsingType.NORMAL) {
      callDeepLink('/product_detail')({
        browsing_type: getAdjustedBrowsingType(meta_matching_browsing_type, meta_matching_pdp_url),
        url: meta_matching_pdp_url,
        shop_id,
        shop_product_no: null,
        catalog_product_id: product_metadata.meta_matching_product_id,
      });
    } else {
      callDeepLink(`${meta_matching_pdp_url}`)();
    }
  }, [
    meta_matching_pdp_url,
    clickGeneralProductLandingLinkUBL,
    catalog_product_id,
    meta_matching_zigzin_product_id,
    meta_matching_browsing_type,
    shop_id,
  ]);

  /**
   * Impression 로그를 전송해야 하는지 판단
   *
   * 웹: 즉시 전송
   * 네이티브 앱:
   * - Android: isResize && (최초 오픈 또는 재오픈 with visibility)
   * - iOS: visibility가 true일 때
   */
  const shouldSendImpressionLog = useCallback((): boolean => {
    const isAnd = isAndroid();
    const isIos = isIOS();
    const isNativePdp = compareWithAppVersion('8.0.0');

    // 웹에서는 즉시 전송
    if (!isNativePdp) {
      return true;
    }

    // AOS 에서 옵션이 미노출된 상태에서도 visibility 값이 visible 이라서
    // 초기 오픈 여부를 isResize 로 판단 (옵션 오픈 전 width 0)
    if (isAnd) {
      const isNativeAndroidPdpFirstOpen = isNativePdp && isResize;
      const isNativeAndroidPdpReOpen = isNativeAndroidPdpFirstOpen && visibility;
      return !!(isNativeAndroidPdpFirstOpen || isNativeAndroidPdpReOpen);
    }

    // iOS는 visibility로 판단
    if (isIos) {
      return !!(isNativePdp && visibility);
    }

    return false;
  }, [isResize, visibility]);

  /**
   * 옵션 노출 UBL 로그 전송
   *
   * 품절: impressSoldOutUBL
   * 판매중: impressOptionUBL (배송 타입, 매칭 상품 정보 포함)
   */
  const sendOptionImpressionLog = useCallback(
    (option: OptionData, isRestockButtonActive: boolean) => {
      // MEMO: 재입고 알림 버튼은 알림 신청이 되어있지 않는 경우에만 로그 전송
      if (option.stock_status?.is_sold_out && !hiddenRestockNotiButton && !isRestockButtonActive) {
        impressSoldOutUBL(catalog_product_id, option.name);
        return;
      }

      const isDeliveryTypeZigzin = checkZigzinOrVendorFlexDeliveryType(option.shipping?.type ?? null);
      const generalProductId =
        product_metadata.is_zigzin_matching_product && !isDeliveryTypeZigzin
          ? product_metadata.matching_catalog_product_id
          : null;
      const zigzinProductId =
        product_metadata.is_general_matching_product && isDeliveryTypeZigzin
          ? product_metadata.matching_catalog_product_id
          : null;

      impressOptionUBL(option.id, catalog_product_id, generalProductId, zigzinProductId);
    },
    [
      hiddenRestockNotiButton,
      impressSoldOutUBL,
      catalog_product_id,
      product_metadata.is_zigzin_matching_product,
      product_metadata.matching_catalog_product_id,
      product_metadata.is_general_matching_product,
      impressOptionUBL,
    ],
  );

  /**
   * 옵션 노출 로그 핸들러
   *
   * 네이티브 앱 조건을 확인한 후 품절/판매중 옵션에 따라 적절한 UBL 로그 전송
   */
  const handleOptionImpress = useCallback(
    (option: OptionData, isRestockButtonActive: boolean) => {
      // 네이티브 앱 조건 확인
      if (!shouldSendImpressionLog()) {
        return;
      }

      // UBL 로그 전송
      sendOptionImpressionLog(option, isRestockButtonActive);
    },
    [shouldSendImpressionLog, sendOptionImpressionLog],
  );

  // 메인 옵션 리스트
  const option_dropdown_list = useMemo(() => {
    if (!state.option.info?.catalog_product.product_option_list || !state.option.map) {
      return [];
    }

    return createOptions({
      option_info: state.option.info,
      option_map: state.option.map,
      selection: state.selection,
      is_only_zigzin_mode: state.ui.is_only_zigzin_option_active,
      restock_selection_mode_activated_index: state.ui.restock_notification_start_index,
      hiddenRestockNotiButton,
      handleOptionSelect,
      handleOptionImpress,
      handleRestockNotification,
      createBaseSelectBoxItemData: config?.createBaseSelectBoxItemData,
    });
  }, [
    state.option.info,
    state.option.map,
    state.selection,
    state.ui.is_only_zigzin_option_active,
    state.ui.restock_notification_start_index,
    hiddenRestockNotiButton,
    handleOptionSelect,
    handleOptionImpress,
    handleRestockNotification,
    config?.createBaseSelectBoxItemData,
  ]);

  // 일반 상품 랜딩 링크 표시 여부
  const display_general_product_landing_link = useMemo(
    () =>
      context.is_zigzag_app &&
      !config.hidden_flags?.general_product_landing_link &&
      product_metadata.is_zigzin_product &&
      product_metadata.is_matching_link_available &&
      !state.ui.is_restock_selection_mode &&
      hasSoldOutOptionsInCurrentSelection(option_dropdown_list, state.selection.selected_dropdown_index),
    [
      context.is_zigzag_app,
      config.hidden_flags?.general_product_landing_link,
      product_metadata.is_zigzin_product,
      product_metadata.is_matching_link_available,
      state.ui.is_restock_selection_mode,
      option_dropdown_list,
      state.selection.selected_dropdown_index,
    ],
  );

  // 일반 상품 랜딩 링크 노출 로그
  useEffect(() => {
    if (isRevealed) {
      impressGeneralProductLandingLinkUBL(catalog_product_id, meta_matching_zigzin_product_id);
    }
  }, [isRevealed]);

  // 초기에는 옵션 오픈 애니메이션 비활성화하고 로드 완료 후 활성화
  useEffect(() => {
    setOpenAnimation(true);
  }, []);

  return {
    display: !config.hidden_flags?.option_dropdown && !emptyMainOption,
    display_header_text: option_dropdown_list.length > 1 && !state.ui.is_restock_selection_mode,
    display_general_product_landing_link,
    displayPrice: !(config.hidden_flags?.select_box_price ?? false),
    displayRestockButton: !(config.hidden_flags?.select_box_restock_button ?? false),
    hiddenGeneralProductLandingLink: config.hidden_flags?.general_product_landing_link,
    option_dropdown_list,
    dropdown_states: state.dropdown_states,
    openAnimation,
    actions: {
      setGeneralProductLandingButtonRef: targetRef,
      handleClickSelectBoxDropdown,
      handleGeneralProductLandingLinkClick,
    },
  };
};
